// A self-contained alarm tone for the operator app — a repeating two-note chime
// synthesised with the Web Audio API, so there is no audio file to ship and it
// works offline. It rings until stopAlarm() is called.
//
// Browsers block audio until the page has had a user gesture, so primeAlarm()
// is wired to the first pointer/key event to unlock (and keep warm) the context.

let ctx: AudioContext | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function getCtx(): AudioContext | null {
  try {
    const Ctor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctx) ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

function tone(c: AudioContext, at: number, freq: number, dur: number) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + dur + 0.03);
}

function ringOnce() {
  const c = getCtx();
  if (!c) return;
  void c.resume().catch(() => {});
  const t = c.currentTime;
  tone(c, t, 880, 0.2); // A5
  tone(c, t + 0.28, 1174.66, 0.22); // D6
}

/** Call on a user gesture so the browser lets us play audio later. */
export function primeAlarm(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume().catch(() => {});
}

/** Start ringing (idempotent — a second call while ringing does nothing). */
export function startAlarm(): void {
  if (timer != null) return;
  ringOnce();
  timer = setInterval(ringOnce, 1600);
}

/** Stop ringing. */
export function stopAlarm(): void {
  if (timer != null) {
    clearInterval(timer);
    timer = null;
  }
}
